/**
 * The missing-labels Fix menu's update-catalog action ("Update labels in
 * Business Goals step" in the create-task wizard, "Update labels in task" in
 * the Add Data modal on an existing task; "entities" for NER) syncs the task
 * label catalog to the uploaded data: unknown extracted labels/entities are
 * added, and — in the wizard only — catalog entries that appear in none of
 * the uploaded files are removed.
 */

/**
 * Next task label/entity catalog.
 *
 * A catalog label is kept when at least one uploaded file covers it — some
 * extracted label resolves to it through Replace-or-create (dropped rows
 * excluded, opted-out forms ignored; same rule as the missing-labels banner
 * in DatasetDetailsForm). The task's fallback label gets no special
 * treatment: a fallback no file holds is removed like any other label, and
 * the caller tells the user (`lostLabelChoiceCopy`). `addedLabels` are
 * appended afterwards, deduplicated, preserving order. When no form has
 * extracted labels there is nothing to judge removals by, so the catalog is
 * only added to.
 *
 * With `removeUncovered: false` (Add Data modal on an existing task) the
 * catalog is only added to: files uploaded to the task earlier may cover
 * labels the modal never sees, so nothing may be removed.
 *
 * @param {object} params
 * @param {string[]} params.catalog Current task labels/entities.
 * @param {string[]} params.addedLabels Unknown extracted labels being added.
 * @param {Array<object>} params.detailsList Active details forms (one per file
 *   in split mode, one per target otherwise).
 * @param {boolean} [params.removeUncovered] Drop catalog labels no uploaded
 *   file covers (create-task wizard). Default true.
 * @returns {string[]} The next catalog.
 */
export function syncTaskLabelCatalog({ catalog, addedLabels, detailsList, removeUncovered = true }) {
    const covered = new Set(addedLabels || []);
    let anyExtracted = false;
    (detailsList || []).forEach((details) => {
        if (!details || details.hasLabels === false) return;
        (details.extractedLabels || []).forEach((label) => {
            anyExtracted = true;
            const mapped = details.labelMappings?.[label];
            if (mapped === null) return;
            covered.add(mapped ?? label);
        });
    });
    const kept =
        removeUncovered && anyExtracted
            ? (catalog || []).filter((label) => covered.has(label))
            : [...(catalog || [])];
    const seen = new Set(kept);
    const next = [...kept];
    (addedLabels || []).forEach((label) => {
        if (!label || seen.has(label)) return;
        seen.add(label);
        next.push(label);
    });
    return next;
}
